import { useMemo } from 'react';
import { Alert, SimpleGrid } from '@mantine/core';
import type { UseQueryResult } from '@tanstack/react-query';
import { COMPONENT_PROJECTS } from '../../../config/componentProjects';
import type { ComponentProjectMetricsResponse } from '../../../types/dashboard';
import { extractErrorMessage } from '../../../utils/notifications';
import { PerformanceCardSkeleton } from '../PerformanceCardSkeleton';
import { hasArchCompanyMetricsData, isArchCompanyMetric, mapArchMetricsData } from '../arch/mapArchMetricsData';
import { AswpComponentProjectMetrics } from './aswp/AswpComponentProjectMetrics';
import {
  hasAswpInsightsData,
  hasAswpProgramParticipationData,
  isAswpInsightsMetric,
  isAswpProgramMetric,
  mapAswpComponentProjectData,
} from './aswp/mapAswpComponentProjectData';
import { ArchComponentProjectMetrics } from './arch/ArchComponentProjectMetrics';
import { ComponentProjectMetricChartCard } from './ComponentProjectMetricChartCard';
import { SafeTComponentProjectMetrics } from './safeT/SafeTComponentProjectMetrics';
import { mapSafeTComponentProjectData } from './safeT/mapSafeTComponentProjectData';

export const COMPONENT_PROJECT_SELECT_OPTIONS = [...COMPONENT_PROJECTS]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((project) => ({ value: project.id, label: project.name }));

/** @deprecated Use COMPONENT_PROJECT_SELECT_OPTIONS */
export const COMPONENT_PROJECT_METRIC_OPTIONS = COMPONENT_PROJECT_SELECT_OPTIONS;

export function getMetricsFreshnessTimestamp(
  data: ComponentProjectMetricsResponse | undefined
): Date | null {
  if (!data) return null;

  let latestMetricUpdate: number | null = null;
  for (const section of data.sections ?? []) {
    for (const metric of section.metrics) {
      if (!metric.updatedAt) continue;
      const timestamp = new Date(metric.updatedAt).getTime();
      if (Number.isNaN(timestamp)) continue;
      if (latestMetricUpdate === null || timestamp > latestMetricUpdate) {
        latestMetricUpdate = timestamp;
      }
    }
  }

  return latestMetricUpdate !== null ? new Date(latestMetricUpdate) : null;
}

export function hasComponentProjectMetricsContent(
  projectId: string,
  data: ComponentProjectMetricsResponse | undefined
): boolean {
  if (!data) return false;

  const id = projectId.toLowerCase();

  if (id === 'safe-t') {
    return mapSafeTComponentProjectData(data) !== null;
  }

  if (id === 'aswp') {
    const aswpData = mapAswpComponentProjectData(data);
    const otherMetrics =
      data.sections?.flatMap((section) =>
        section.metrics.filter(
          (metric) =>
            !isAswpProgramMetric(metric.metricId, metric) &&
            !isAswpInsightsMetric(metric.metricId, metric)
        )
      ) ?? [];
    const showProgramParticipation =
      aswpData !== null && hasAswpProgramParticipationData(aswpData);
    const showInsights = aswpData !== null && hasAswpInsightsData(aswpData);
    return showProgramParticipation || showInsights || otherMetrics.length > 0;
  }

  if (id === 'arch') {
    const archData = mapArchMetricsData(data);
    const otherMetrics =
      data.sections?.flatMap((section) =>
        section.metrics.filter((metric) => !isArchCompanyMetric(metric.metricId, metric))
      ) ?? [];
    const showCompanyMetrics = archData !== null && hasArchCompanyMetricsData(archData);
    return showCompanyMetrics || otherMetrics.length > 0;
  }

  return (data.sections ?? []).some((section) => section.metrics.length > 0);
}

type AdminComponentProjectMetricsProps = {
  projectId: string;
  metricsQuery: UseQueryResult<ComponentProjectMetricsResponse, unknown>;
};

export function AdminComponentProjectMetrics({
  projectId,
  metricsQuery,
}: AdminComponentProjectMetricsProps) {
  const { data, isLoading, isFetching, isError, error } = metricsQuery;
  const isSafeT = projectId.toLowerCase() === 'safe-t';
  const isAswp = projectId.toLowerCase() === 'aswp';
  const isArch = projectId.toLowerCase() === 'arch';

  const metricsToRender = useMemo(() => {
    if (!data?.sections?.length) return [];
    return data.sections.flatMap((section) =>
      section.metrics.map((metric) => ({
        key: `${section.sectionId}-${metric.metricId}`,
        metric,
        sectionTitle: section.title,
      }))
    );
  }, [data]);

  return (
    <>
      {isError ? (
        <Alert title="Could not load metrics" color="red">
          {extractErrorMessage(error, 'Unable to fetch metrics. Please try again.')}
        </Alert>
      ) : null}

      {isLoading || isFetching ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[{ maxWidth: 1200, cols: 1 }]}
          role="status"
          aria-busy="true"
          aria-label="Loading metrics"
        >
          <PerformanceCardSkeleton />
          <PerformanceCardSkeleton />
        </SimpleGrid>
      ) : null}

      {!isLoading && !isFetching && !isError && isSafeT && data ? (
        <SafeTComponentProjectMetrics data={data} />
      ) : null}

      {!isLoading && !isFetching && !isError && isAswp && data ? (
        <AswpComponentProjectMetrics data={data} />
      ) : null}

      {!isLoading && !isFetching && !isError && isArch && data ? (
        <ArchComponentProjectMetrics data={data} />
      ) : null}

      {!isLoading && !isFetching && !isError && !isSafeT && !isAswp && !isArch && metricsToRender.length > 0 ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[{ maxWidth: 1200, cols: 1 }]}
          sx={{ alignItems: 'stretch' }}
        >
          {metricsToRender.map(({ key, metric, sectionTitle }) => (
            <ComponentProjectMetricChartCard
              key={key}
              metric={metric}
              sectionTitle={sectionTitle}
            />
          ))}
        </SimpleGrid>
      ) : null}
    </>
  );
}
